import { useLayoutEffect } from 'react';
import './ChatKeyboardViewport.css';

export default function useChatKeyboardViewport(shell) {
  useLayoutEffect(() => {
    const node = shell.current;
    if (!node) return undefined;
    const viewport = window.visualViewport;
    const navbar = document.querySelector('.navbar.fixed, .artist-navbar');
    let frame = null, focusTimer = null;
    let settleTimers = [];
    let baselineHeight = viewport?.height || window.innerHeight;
    let lastGeometry = '';
    const inputFocused = () => Boolean(node.contains(document.activeElement) && document.activeElement?.matches('.ae-connect-compose input, .ae-connect-compose textarea'));
    const measure = () => {
      frame = null;
      const visibleTop = Math.max(0, viewport?.offsetTop || 0);
      const visibleHeight = viewport?.height || window.innerHeight;
      const focused = inputFocused();
      if (!focused) baselineHeight = visibleHeight;
      const visibleBottom = visibleTop + visibleHeight;
      const navBottom = navbar?.getBoundingClientRect().bottom || 0;
      // Fixed elements use layout coordinates; the keyboard can also pan the
      // visible viewport, so move both the top and the available bottom edge.
      const normalTop = Math.max(visibleTop + 8, navBottom + 8);
      const top = Math.max(visibleTop + 4, Math.min(normalTop, visibleBottom - 72));
      const height = Math.max(0, visibleBottom - top - 8);
      const keyboardOpen = focused && baselineHeight - visibleHeight > 80;
      const geometry = [top.toFixed(2), height.toFixed(2), keyboardOpen].join(':');
      if (geometry === lastGeometry) return;
      lastGeometry = geometry;
      const messages = node.querySelector('.ae-connect-messages');
      const atBottom = messages && messages.scrollHeight - messages.scrollTop - messages.clientHeight < 60;
      node.style.top = top + 'px';
      node.style.setProperty('--ae-chat-height', height + 'px');
      node.classList.toggle('ae-chat-short-viewport', height < 160);
      node.classList.toggle('ae-chat-keyboard-open', keyboardOpen);
      if (atBottom) messages.scrollTop = messages.scrollHeight;
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(measure);
    };
    const focusChanged = () => {
      clearInterval(focusTimer);
      focusTimer = inputFocused() ? setInterval(schedule, 150) : null;
      settleTimers.forEach(clearTimeout);
      // WebKit can report final keyboard geometry after the focus event.
      settleTimers = [50, 150, 300, 550, 900].map(ms => setTimeout(schedule, ms));
      schedule();
    };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (navbar) observer?.observe(navbar);
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', focusChanged);
    window.addEventListener('pageshow', schedule);
    node.addEventListener('focusin', focusChanged);
    node.addEventListener('focusout', focusChanged);
    measure();
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      clearInterval(focusTimer);
      settleTimers.forEach(clearTimeout);
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', focusChanged);
      window.removeEventListener('pageshow', schedule);
      node.removeEventListener('focusin', focusChanged);
      node.removeEventListener('focusout', focusChanged);
      observer?.disconnect();
      node.classList.remove('ae-chat-short-viewport', 'ae-chat-keyboard-open');
    };
  }, [shell]);
}
